/* =====================================================================
 * useDocxEditor — DOCX 편집 상태 관리 훅
 * 문서 모델 관리, dirty 블록 추적, undo/redo, 서식 적용
 *
 * 핵심 원칙: 편집 중 DOM이 source of truth.
 * React 상태 동기화는 다운로드/서식 변경 등 명시적 시점에만 수행.
 * ===================================================================== */

import { useState, useCallback, useRef, useEffect } from 'react';
import { parseDocx } from '@auw/workspace-utils';
import type {
  DocxDocument,
  DocxBlock,
  DocxParagraph,
  DocxRun,
  DocxTable,
} from '@auw/workspace-utils';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

export interface FormatState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  fontSize: number | null;
  fontFamily: string | null;
  color: string | null;
  alignment: string | null;
}

interface UndoEntry {
  body: DocxBlock[];
  dirtyIds: Set<string>;
  description: string;
}

export interface DocxEditorState {
  document: DocxDocument | null;
  loading: boolean;
  error: string | null;
  isDirty: boolean;
  formatState: FormatState;
  canUndo: boolean;
  canRedo: boolean;
}

export interface DocxEditorActions {
  /** DOM → 모델 동기화 후 문서를 반환 (다운로드 전 호출) */
  syncAndGetDocument: (domTexts: Map<string, string>) => DocxDocument | null;
  applyFormat: (format: Partial<FormatState>) => void;
  undo: () => void;
  redo: () => void;
  getDocument: () => DocxDocument | null;
  getDirtyBlockIds: () => Set<string>;
  /** 커서 위치의 서식 상태 갱신 (selectionchange 이벤트에서 호출) */
  updateFormatFromCursor: () => void;
  /** 번호매기기 단락 뒤에 새 번호매기기 단락 삽입. 새 단락 ID 반환 */
  insertNumberedParagraphAfter: (blockId: string, domTexts: Map<string, string>) => string | null;
  /** 단락에서 번호매기기 제거 */
  removeNumbering: (blockId: string) => void;
}

const DEFAULT_FORMAT: FormatState = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  fontSize: null,
  fontFamily: null,
  color: null,
  alignment: null,
};

const MAX_UNDO_STACK = 50;

/* ------------------------------------------------------------------ */
/* Hook                                                                */
/* ------------------------------------------------------------------ */

export function useDocxEditor(
  fileBuffer: ArrayBuffer | null,
  onLoadComplete?: () => void,
  onError?: (msg: string) => void,
): [DocxEditorState, DocxEditorActions] {
  const [document, setDocument] = useState<DocxDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formatState, setFormatState] = useState<FormatState>(DEFAULT_FORMAT);
  const [undoIndex, setUndoIndex] = useState(-1);

  const dirtyIds = useRef(new Set<string>());
  const undoStack = useRef<UndoEntry[]>([]);

  /* ── 파일 파싱 ── */
  useEffect(() => {
    if (!fileBuffer) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    parseDocx(fileBuffer)
      .then((doc) => {
        if (cancelled) return;
        setDocument(doc);
        dirtyIds.current = new Set();
        undoStack.current = [];
        setUndoIndex(-1);
        setLoading(false);
        onLoadComplete?.();
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useDocxEditor] Parse error:', err);
        setError('DOCX 파싱에 실패했습니다.');
        setLoading(false);
        onError?.('DOCX 파싱에 실패했습니다.');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileBuffer]);

  /* ── 스냅샷 저장 (undo용) ── */
  const pushSnapshot = useCallback(
    (description: string) => {
      if (!document) return;

      const currentIdx = undoIndex;
      const newStack = undoStack.current.slice(0, currentIdx + 1);
      const bodyClone = JSON.parse(JSON.stringify(document.body)) as DocxBlock[];
      const dirtyClone = new Set(dirtyIds.current);

      newStack.push({ body: bodyClone, dirtyIds: dirtyClone, description });

      if (newStack.length > MAX_UNDO_STACK) {
        newStack.shift();
      }

      undoStack.current = newStack;
      setUndoIndex(newStack.length - 1);
    },
    [document, undoIndex],
  );

  /* ── DOM → 모델 동기화 ── */
  const syncAndGetDocument = useCallback(
    (domTexts: Map<string, string>): DocxDocument | null => {
      if (!document) return null;

      // DOM 텍스트와 모델 텍스트가 다른 블록만 업데이트
      const updatedBody = applyDomTextsToBody(document.body, domTexts, dirtyIds.current);

      const updatedDoc = { ...document, body: updatedBody };
      // 내부 상태도 반영 (다음 서식 적용 등에 사용)
      setDocument(updatedDoc);
      return updatedDoc;
    },
    [document],
  );

  /* ── 서식 적용 (선택된 텍스트에) ── */
  const applyFormat = useCallback(
    (format: Partial<FormatState>) => {
      if (!document) return;

      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;

      const anchorEl = sel.anchorNode?.parentElement?.closest('[data-block-id]');
      if (!anchorEl) return;

      const blockId = anchorEl.getAttribute('data-block-id');
      if (!blockId) return;

      pushSnapshot('서식 변경');

      let updatedBody = document.body;

      // alignment는 단락 수준
      if (format.alignment !== undefined) {
        updatedBody = updatedBody.map((block) => {
          if (block.type === 'paragraph' && block.id === blockId) {
            return { ...block, alignment: format.alignment || undefined, dirty: true } as DocxParagraph;
          }
          if (block.type === 'table') {
            return updateTableParagraphFormat(block, blockId, format);
          }
          return block;
        });
        dirtyIds.current.add(blockId);
      }

      // 나머지 서식은 런 수준
      const runFormats = { ...format };
      delete runFormats.alignment;
      if (Object.keys(runFormats).length > 0) {
        updatedBody = updatedBody.map((block) => {
          if (block.type === 'paragraph' && block.id === blockId) {
            const updatedRuns = block.runs.map((run) => {
              if (run.type !== 'text') return run;
              return applyRunFormat(run, runFormats);
            });
            return { ...block, runs: updatedRuns, dirty: true } as DocxParagraph;
          }
          if (block.type === 'table') {
            return applyTableRunFormat(block, blockId, runFormats);
          }
          return block;
        });
        dirtyIds.current.add(blockId);
      }

      setDocument({ ...document, body: updatedBody });
      setFormatState((prev) => ({ ...prev, ...format }));
    },
    [document, pushSnapshot],
  );

  /* ── Undo ── */
  const undo = useCallback(() => {
    if (undoIndex < 0 || !document) return;

    if (undoIndex === undoStack.current.length - 1) {
      const bodyClone = JSON.parse(JSON.stringify(document.body)) as DocxBlock[];
      const dirtyClone = new Set(dirtyIds.current);
      undoStack.current.push({ body: bodyClone, dirtyIds: dirtyClone, description: 'current' });
    }

    const entry = undoStack.current[undoIndex];
    if (!entry) return;

    setDocument({ ...document, body: JSON.parse(JSON.stringify(entry.body)) });
    dirtyIds.current = new Set(entry.dirtyIds);
    setUndoIndex(undoIndex - 1);
  }, [document, undoIndex]);

  /* ── Redo ── */
  const redo = useCallback(() => {
    const nextIdx = undoIndex + 2;
    if (nextIdx >= undoStack.current.length || !document) return;

    const entry = undoStack.current[nextIdx];
    if (!entry) return;

    setDocument({ ...document, body: JSON.parse(JSON.stringify(entry.body)) });
    dirtyIds.current = new Set(entry.dirtyIds);
    setUndoIndex(nextIdx - 1);
  }, [document, undoIndex]);

  /* ── 커서 위치에서 서식 상태 읽기 ── */
  const updateFormatFromCursor = useCallback(() => {
    if (!document) return;

    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const anchorNode = sel.anchorNode;
    if (!anchorNode) return;

    // 가장 가까운 블록 요소 찾기
    const blockEl = (anchorNode.nodeType === 1
      ? anchorNode as Element
      : anchorNode.parentElement
    )?.closest('[data-block-id]');
    if (!blockEl) return;

    const blockId = blockEl.getAttribute('data-block-id');
    if (!blockId) return;

    // 모델에서 해당 블록 찾기
    const findBlock = (blocks: DocxBlock[]): DocxParagraph | null => {
      for (const b of blocks) {
        if (b.type === 'paragraph' && b.id === blockId) return b;
        if (b.type === 'table') {
          for (const row of b.rows) {
            for (const cell of row.cells) {
              for (const para of cell.paragraphs) {
                if (para.id === blockId) return para;
              }
            }
          }
        }
      }
      return null;
    };

    const para = findBlock(document.body);
    if (!para) return;

    const defaults = document.docDefaults;

    // ── 커서 위치에 해당하는 런 찾기 ──
    // DOM에서 data-run-index 속성을 가진 가장 가까운 span 찾기
    const runEl = (anchorNode.nodeType === 1
      ? anchorNode as Element
      : anchorNode.parentElement
    )?.closest('[data-run-index]');

    let cursorRun: DocxRun | undefined;
    if (runEl) {
      const runIdx = parseInt(runEl.getAttribute('data-run-index') || '0', 10);
      if (runIdx >= 0 && runIdx < para.runs.length) {
        cursorRun = para.runs[runIdx];
      }
    }
    // 폴백: 첫 번째 텍스트 런
    if (!cursorRun || cursorRun.type !== 'text') {
      cursorRun = para.runs.find(r => r.type === 'text');
    }

    // ── 스타일 체인에서 런 속성 해석 ──
    // 폰트 패밀리: run → style rPr → docDefaults
    let resolvedFontFamily: string | null = cursorRun?.fontFamily || null;
    if (!resolvedFontFamily && para.styleId) {
      const style = document.styles.get(para.styleId);
      if (style?.fontFamily) {
        resolvedFontFamily = style.fontFamily;
      } else if (style?.basedOn) {
        // basedOn 체인 탐색 (최대 5단계)
        let parentId: string | undefined = style.basedOn;
        for (let depth = 0; depth < 5 && parentId; depth++) {
          const parent = document.styles.get(parentId);
          if (parent?.fontFamily) {
            resolvedFontFamily = parent.fontFamily;
            break;
          }
          parentId = parent?.basedOn;
        }
      }
    }
    if (!resolvedFontFamily) {
      resolvedFontFamily = defaults?.fontFamily || null;
    }

    // 폰트 크기: run → style → docDefaults → 20 (10pt)
    let resolvedFontSize = cursorRun?.fontSize || 0;
    if (!resolvedFontSize && para.styleId) {
      const style = document.styles.get(para.styleId);
      if (style?.fontSize) {
        resolvedFontSize = style.fontSize;
      } else if (style?.basedOn) {
        let parentId: string | undefined = style.basedOn;
        for (let depth = 0; depth < 5 && parentId; depth++) {
          const parent = document.styles.get(parentId);
          if (parent?.fontSize) {
            resolvedFontSize = parent.fontSize;
            break;
          }
          parentId = parent?.basedOn;
        }
      }
    }
    if (!resolvedFontSize) resolvedFontSize = defaults?.fontSize || 20;
    const fontSizePt = resolvedFontSize / 2; // half-points → points

    // 정렬: paragraph → style → docDefaults
    let resolvedAlignment = para.alignment || null;
    if (!resolvedAlignment && para.styleId) {
      const style = document.styles.get(para.styleId);
      if (style?.alignment) resolvedAlignment = style.alignment;
    }
    if (!resolvedAlignment && defaults?.alignment) {
      resolvedAlignment = defaults.alignment;
    }

    setFormatState({
      bold: cursorRun?.bold || false,
      italic: cursorRun?.italic || false,
      underline: cursorRun?.underline || false,
      strike: cursorRun?.strike || false,
      fontSize: fontSizePt,
      fontFamily: resolvedFontFamily,
      color: cursorRun?.color || null,
      alignment: resolvedAlignment,
    });
  }, [document]);

  /* ── 번호매기기 단락 뒤에 새 단락 삽입 ── */
  const insertNumberedParagraphAfter = useCallback(
    (blockId: string, domTexts: Map<string, string>): string | null => {
      if (!document) return null;

      // 먼저 DOM 텍스트 동기화
      const updatedBody = applyDomTextsToBody(document.body, domTexts, dirtyIds.current);

      // 해당 블록 찾기 & 인덱스
      const idx = updatedBody.findIndex(
        (b) => b.type === 'paragraph' && b.id === blockId,
      );
      if (idx === -1) return null;

      const currentPara = updatedBody[idx] as DocxParagraph;
      if (!currentPara.numbering || currentPara.numbering.numId === '0') return null;

      // 새 단락 ID
      const newId = `docx_new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

      // 새 번호매기기 단락 생성 (같은 numId, level, 다음 index)
      const newPara: DocxParagraph = {
        type: 'paragraph',
        id: newId,
        styleId: currentPara.styleId,
        alignment: currentPara.alignment,
        indent: currentPara.indent,
        spacing: currentPara.spacing,
        runs: [],
        numbering: {
          numId: currentPara.numbering.numId,
          level: currentPara.numbering.level,
          index: currentPara.numbering.index + 1,
        },
        dirty: true,
      };

      // 삽입 위치 이후 같은 numId+level의 인덱스 업데이트
      const newBody = [...updatedBody];
      newBody.splice(idx + 1, 0, newPara);

      // 이후 같은 numId+level 단락의 index 증가
      const { numId, level } = currentPara.numbering;
      for (let i = idx + 2; i < newBody.length; i++) {
        const b = newBody[i];
        if (
          b.type === 'paragraph' &&
          b.numbering &&
          b.numbering.numId === numId &&
          b.numbering.level === level
        ) {
          newBody[i] = {
            ...b,
            numbering: { ...b.numbering, index: b.numbering.index + 1 },
          };
        }
      }

      dirtyIds.current.add(newId);
      setDocument({ ...document, body: newBody });
      return newId;
    },
    [document],
  );

  /* ── 단락에서 번호매기기 제거 ── */
  const removeNumbering = useCallback(
    (blockId: string) => {
      if (!document) return;

      const newBody = document.body.map((block) => {
        if (block.type === 'paragraph' && block.id === blockId) {
          return {
            ...block,
            indent: undefined,
            // style 기반 목록도 다시 나타나지 않도록 OOXML 해제 sentinel을 보존한다.
            numbering: {
              numId: '0',
              level: block.numbering?.level ?? 0,
              index: 0,
            },
            dirty: true,
          };
        }
        return block;
      });

      dirtyIds.current.add(blockId);
      setDocument({ ...document, body: newBody });
    },
    [document],
  );

  /* ── Getters ── */
  const getDocument = useCallback(() => document, [document]);
  const getDirtyBlockIds = useCallback(() => new Set(dirtyIds.current), []);

  const state: DocxEditorState = {
    document,
    loading,
    error,
    isDirty: dirtyIds.current.size > 0,
    formatState,
    canUndo: undoIndex >= 0,
    canRedo: undoIndex + 2 < undoStack.current.length,
  };

  const actions: DocxEditorActions = {
    syncAndGetDocument,
    applyFormat,
    undo,
    redo,
    getDocument,
    getDirtyBlockIds,
    updateFormatFromCursor,
    insertNumberedParagraphAfter,
    removeNumbering,
  };

  return [state, actions];
}

/* ------------------------------------------------------------------ */
/* 내부 헬퍼                                                            */
/* ------------------------------------------------------------------ */

/** DOM 텍스트를 body 모델에 반영 (변경된 블록만 업데이트) */
function applyDomTextsToBody(
  body: DocxBlock[],
  domTexts: Map<string, string>,
  dirtyIds: Set<string>,
): DocxBlock[] {
  return body.map((block) => {
    if (block.type === 'paragraph') {
      const domText = domTexts.get(block.id);
      if (domText === undefined) return block;
      const modelText = block.runs.map((r) => r.text || '').join('');
      if (domText === modelText) return block;

      // 텍스트가 달라졌으면 업데이트
      dirtyIds.add(block.id);
      const firstRun = block.runs[0];
      const newRuns: DocxRun[] = [{
        type: 'text',
        id: firstRun?.id || `run_synced_${Date.now()}`,
        text: domText,
        ...(firstRun ? {
          bold: firstRun.bold,
          italic: firstRun.italic,
          underline: firstRun.underline,
          strike: firstRun.strike,
          fontSize: firstRun.fontSize,
          fontFamily: firstRun.fontFamily,
          color: firstRun.color,
        } : {}),
      }];
      return { ...block, runs: newRuns, dirty: true } as DocxParagraph;
    }

    if (block.type === 'table') {
      let changed = false;
      const updatedRows = block.rows.map((row) => ({
        ...row,
        cells: row.cells.map((cell) => ({
          ...cell,
          paragraphs: cell.paragraphs.map((para) => {
            const domText = domTexts.get(para.id);
            if (domText === undefined) return para;
            const modelText = para.runs.map((r) => r.text || '').join('');
            if (domText === modelText) return para;

            changed = true;
            dirtyIds.add(para.id);
            const firstRun = para.runs[0];
            const newRuns: DocxRun[] = [{
              type: 'text',
              id: firstRun?.id || `run_synced_${Date.now()}`,
              text: domText,
              ...(firstRun ? {
                bold: firstRun.bold,
                italic: firstRun.italic,
                underline: firstRun.underline,
                strike: firstRun.strike,
                fontSize: firstRun.fontSize,
                fontFamily: firstRun.fontFamily,
                color: firstRun.color,
              } : {}),
            }];
            return { ...para, runs: newRuns, dirty: true } as DocxParagraph;
          }),
        })),
      }));
      return changed ? { ...block, rows: updatedRows, dirty: true } : block;
    }

    return block;
  });
}

function updateTableParagraphFormat(
  table: DocxTable,
  blockId: string,
  format: Partial<FormatState>,
): DocxTable {
  let changed = false;
  const updatedRows = table.rows.map((row) => ({
    ...row,
    cells: row.cells.map((cell) => ({
      ...cell,
      paragraphs: cell.paragraphs.map((para) => {
        if (para.id === blockId) {
          changed = true;
          return {
            ...para,
            alignment: format.alignment || para.alignment,
            dirty: true,
          } as DocxParagraph;
        }
        return para;
      }),
    })),
  }));

  return changed ? { ...table, rows: updatedRows, dirty: true } : table;
}

function applyRunFormat(run: DocxRun, format: Partial<FormatState>): DocxRun {
  const updated = { ...run };
  if (format.bold !== undefined) updated.bold = format.bold;
  if (format.italic !== undefined) updated.italic = format.italic;
  if (format.underline !== undefined) updated.underline = format.underline;
  if (format.strike !== undefined) updated.strike = format.strike;
  if (format.fontSize !== undefined && format.fontSize !== null) updated.fontSize = format.fontSize * 2;
  if (format.fontFamily !== undefined) updated.fontFamily = format.fontFamily || undefined;
  if (format.color !== undefined) updated.color = format.color || undefined;
  return updated;
}

function applyTableRunFormat(
  table: DocxTable,
  blockId: string,
  runFormats: Partial<FormatState>,
): DocxTable {
  let changed = false;
  const updatedRows = table.rows.map((row) => ({
    ...row,
    cells: row.cells.map((cell) => ({
      ...cell,
      paragraphs: cell.paragraphs.map((para) => {
        if (para.id === blockId) {
          changed = true;
          return {
            ...para,
            runs: para.runs.map((r) => r.type === 'text' ? applyRunFormat(r, runFormats) : r),
            dirty: true,
          } as DocxParagraph;
        }
        return para;
      }),
    })),
  }));

  return changed ? { ...table, rows: updatedRows, dirty: true } : table;
}
